import React, { useEffect, useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  getExpandedRowModel,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { Button } from '../ui/button'
import {
  ArrowRightCircleIcon,
  ChevronDownIcon,
  HomeIcon,
  HomeModernIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/16/solid'
import useGetMoneyPaths from '@/services/api/money-path/useGetMoneyPaths'
// import DeleteMoneyPathModal from './DeleteMoneyPathModal'
import { Link, useSearchParams } from 'react-router-dom'
import useGetTodos from '@/services/api/to-do/useGetTodos'
import { formatDate } from 'date-fns'
import MyTodoTransactionsTable from './MyTodoTransactionsTable'
import { HandCoins, LucideHome } from 'lucide-react'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import useAppStore from '@/store/useAppStore'

const columnHelper = createColumnHelper()

function ColumnHeader({ children, className, ...rest }) {
  return (
    <div
      className={cn(['tw-font-medium tw-text-primary px-4'], className)}
      {...rest}
    >
      {children}
    </div>
  )
}

export default function MyTodosTable() {
  const { data: todos } = useGetTodos()
  const [searchParams, setSearchParams] = useSearchParams()
  const _fromVault = searchParams.get('from-vault') // in|out|all
  const _toVault = searchParams.get('to-vault') // in|out|all

  const selectedMyTodoTransactionRowId = useAppStore(
    (state) => state.selectedMyTodoTransactionRowId,
  )
  const formatter = useNumberFormatter({
  })
  const rowdata = useMemo(() => {
    const rows = []

    todos?.forEach((todo) => {
      rows.push({
        ...todo,
        name: todo.from_vault_name,
        amount: todo.total,
        test: 'test',
        toVaults: todo.to_vaults.map((x) => ({
          ...x,
          name: x.to_vault_name,
          amount: x.total,
          fromVault: todo.from_vault,
          transactions: x.transactions.map((y) => ({
            ...y,
            name: y.description,
            amount: y.amount,
            transaction_date: formatDate(
              new Date(y.transaction_date),
              'd-MMM yy',
            ),
          })),
        })),
      })
    })

    return rows
  }, [todos])

  useEffect(() => {
    const el = document.getElementById(selectedMyTodoTransactionRowId)
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      })
    }
  }, [])

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: ({ table }) => (
          <ColumnHeader
            className={`group flex h-full w-auto items-center bg-gray-low   py-2  text-txt-secondary`}
          >
            Name
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => (
          <div
            className={
              {
                0: '',
                1: 'pl-6',
                2: 'pl-9',
                3: 'pl-12',
                4: 'pl-15',
              }[row.depth]
            }
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                {/* <HomeModernIcon /> */}
                {/* {row.id} */}
                {row.original.from_vault_name ? (
                  <LucideHome className="size-4 text-txt-placeholder" />
                ) : (
                  <HandCoins className="size-4 text-txt-placeholder" />
                )}
                <span>{row.original.name}</span>
              </div>

              <Button
                iconOnly
                variant="ghost"
                onClick={() => {
                  const currentUrl = new URL(window.location.href)
                  const newParams = new URLSearchParams(currentUrl.search)

                  const expanded = row.getIsExpanded()

                  row.toggleExpanded()

                  if (row.original.from_vault) {
                    if (expanded) {
                      newParams.delete('from-vault')
                    } else {
                      // newParams.set('from-vault', row.original.from_vault)
                      newParams.set('from-vault', row.id)
                    }
                  }

                  if (row.original.to_vault) {
                    if (expanded) {
                      newParams.delete('to-vault')
                    } else {
                      // newParams.set('to-vault', row.original.to_vault)
                      newParams.set('to-vault', row.id)
                    }
                  }

                  window.history.replaceState(
                    null,
                    '',
                    '?' + newParams.toString(),
                  )
                }}
              >
                <ChevronDownIcon />
              </Button>
            </div>
          </div>
        ),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('amount', {
        header: ({ table }) => (
          <ColumnHeader
            // className={`group flex h-full w-auto items-center justify-end  py-2 text-right text-txt-secondary`}
            className={` flex h-full items-center justify-end py-2 text-txt-secondary`}
          >
            <span>Amount</span>
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => (
          <div className="justify-end text-right">{formatter(getValue())}</div>
        ),
        footer: (info) => info.column.id,
      }),

      // columnHelper.accessor('test', {
      //   header: ({ table }) => (
      //     <ColumnHeader
      //       // className={`group flex h-full w-auto items-center justify-end  py-2 text-right text-txt-secondary`}
      //       className={` flex h-full items-center justify-end py-2 text-txt-secondary`}
      //     >
      //       <span>Amount</span>
      //     </ColumnHeader>
      //   ),
      //   enableSorting: true,
      //   cell: ({ row, getValue }) => (
      //     <div className="justify-end text-right">{formatter(getValue())}</div>
      //   ),
      //   footer: (info) => info.column.id,
      // }),
    ],
    [],
  )

  const { data, status } = useGetMoneyPaths()

  const table = useReactTable({
    data: rowdata,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getSubRows: (row) => row.toVaults, // || row.transactions,
    // getRowId: (row) => {
    //   return row.from_vault
    //     ? `from-vault-${row.from_vault}`
    //     : `to-vault-${row.to_vault}`
    // },
    // state: {
    //   columnPinning: {
    //     left: ['id'],
    //   },
    // },
    state: {
      // expanded: {
      //   [`from-vault-${_fromVault}`]: true,
      //   [`to-vault-${_toVault}`]: true,
      // },
    },
    initialState: {
      expanded: {
        [`${_fromVault}`]: true,
        [`${_toVault}`]: true,
      },
      //   columnPinning: {
      //     left: ['name'],
      //   },
      // expanded: true,
    },
  })

  if (status === 'pending') return 'Loading....'

  return (
    <div
      className={cn(
        'overflow-auto rounded-md border transition-all data-[busy]:pointer-events-none data-[busy]:animate-pulse data-[busy]:opacity-50',
      )}
    >
      <div className="grid grid-cols-[250px,156px,110px,156px,1fr,1fr] overflow-auto   [&>div:not(:last-child)]:border-b">
        {table.getHeaderGroups().map((headerGroup) => (
          <div
            data-gid={headerGroup.id}
            className="col-span-full grid h-[60px] grid-cols-subgrid bg-gray-low [&>div:not(:last-child)]:border-r [&>div]:border-solid"
          >
            {headerGroup.headers.map((header, i) => {
              return (
                <div
                  key={header.id}
                  className={`border-none`}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </div>
              )
            })}
          </div>
        ))}
        {/* </div> */}
        {/* <div> */}
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => {
            return (
              <>
                {/* row */}
                <div
                  key={row.id}
                  className={`${row.original.to_vault_name ? 'bg-[#E4F1FF]' : ''} group/row relative col-span-full grid h-[52px] grid-cols-subgrid !opacity-100 [&>div:not(:last-child)]:border-r [&>div:not(:last-child)]:border-solid`}
                >
                  {row.getVisibleCells().map((cell) => (
                    // cell
                    <div
                      key={cell.id}
                      className={`group/cell relative flex items-center border-none px-4 transition-all group-hover/row:bg-opacity-15 [&>div]:flex [&>div]:h-full [&>div]:w-full [&>div]:items-center `}
                      data-first-col={
                        cell.column.getIsFirstColumn() ? true : undefined
                      }
                    >
                      {/* <div
                        data-first-col={
                          cell.column.getIsFirstColumn() ? true : undefined
                        }
                      > */}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                    // </div>
                  ))}
                </div>
                {row.getIsExpanded() && row.original?.transactions && (
                  // row
                  <div className="col-span-full grid grid-cols-subgrid px-5">
                    {/* cell */}
                    <MyTodoTransactionsTable
                      // transactions={row.original.transactions}
                      firstLevelRowId={row.parentId}
                      secondLevelRowId={row.id}
                      toVault={row.original as any}
                    />
                  </div>
                )}
              </>
            )
          })
        ) : (
          <div>
            <div className=" text-center">No results.</div>
          </div>
        )}
      </div>
      {/* </div> */}
    </div>
  )
}
